import { Fab as MuiFab, FabProps as MuiFabProps, SxProps, Theme } from '@mui/material';
import { forwardRef } from 'react';

/** Palette colors that use white contrast text; others use theme contrastText. */
const FAB_COLORS_WITH_WHITE_CONTRAST = [
  'primary',
  'secondary',
  'info',
  'success',
  'error',
] as const;

export type FabColor = (typeof FAB_COLORS_WITH_WHITE_CONTRAST)[number] | 'warning';

export interface FabProps extends Omit<MuiFabProps, 'color'> {
  /**
   * The color of the component. Only palette colors are supported; white text
   * is applied for primary, secondary, info, success, error; warning uses
   * theme contrastText.
   * @default 'primary'
   */
  color?: FabColor;

  /**
   * The size of the component.
   * @default 'large'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * The variant to use.
   * @default 'circular'
   */
  variant?: 'circular' | 'extended';

  /**
   * If true, applies a standard shadow style for FAB buttons.
   * @default true
   */
  withShadow?: boolean;

  /**
   * If true, the FAB will be in an active/pressed state with darker background.
   * @default false
   */
  isActive?: boolean;

  /**
   * Custom styles for the FAB
   */
  sx?: SxProps<Theme>;

  /**
   * The content of the component (typically an icon, or icon + text for extended)
   */
  children: React.ReactNode;

  /**
   * Aria label for accessibility
   */
  'aria-label': string;
}

/**
 * FAB (Floating Action Button) Component
 *
 * A wrapper around MUI Fab with standardized styling and behavior.
 * Includes built-in customizations from the design system:
 * - Standard shadow for elevation
 * - Active/pressed state support with darker background
 * - White text color for better contrast
 * - Consistent hover effects
 *
 * @example
 * ```tsx
 * <Fab aria-label="add" color="primary">
 *   <AddIcon />
 * </Fab>
 * ```
 *
 * @example
 * ```tsx
 * // Extended FAB with text
 * <Fab aria-label="create" variant="extended" color="primary">
 *   <AddIcon sx={{ mr: 1 }} />
 *   Create
 * </Fab>
 * ```
 *
 * @example
 * ```tsx
 * // With active state
 * <Fab aria-label="edit" color="info" isActive={isEditing}>
 *   <EditIcon />
 * </Fab>
 * ```
 */
export const Fab = forwardRef<HTMLButtonElement, FabProps>(
  (
    {
      color = 'primary',
      size = 'large',
      variant = 'circular',
      withShadow = true,
      isActive = false,
      sx,
      'aria-label': ariaLabel,
      children,
      ...props
    },
    ref
  ) => {
    const usesWhiteContrast = FAB_COLORS_WITH_WHITE_CONTRAST.includes(
      color as (typeof FAB_COLORS_WITH_WHITE_CONTRAST)[number]
    );
    const baseStyles: SxProps<Theme> = (theme) => ({
      ...(withShadow && {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.5)',
      }),
      ...(usesWhiteContrast && { color: 'common.white' }),
      ...(isActive && {
        backgroundColor: theme.palette[color].dark,
        '&:hover': {
          backgroundColor: theme.palette[color].dark,
        },
      }),
    });

    return (
      <MuiFab
        ref={ref}
        color={color}
        size={size}
        variant={variant}
        aria-label={ariaLabel}
        sx={[baseStyles, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        {...props}
      >
        {children}
      </MuiFab>
    );
  }
);

Fab.displayName = 'Fab';
